import React, { useState, useEffect, useRef } from 'react'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { BarcodeFormat, DecodeHintType } from '@zxing/library'

const MAX_SHELF_CODE_LENGTH = 32

// Our shelf labels are printed as Code 128 only. Telling zxing the exact
// format up front is faster than letting it try every supported format on
// every frame, and — more importantly — it's measurably more tolerant of
// scuffed / faded labels because the decoder isn't second-guessing which
// format it's looking at.
const ZXING_HINTS = new Map([
  [DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.CODE_128]]
])

// Mobile cameras default to ~640x480, which is not enough pixels per
// stripe for the rubbed-up barcodes we have on warehouse shelves. We ask
// for 1080p; the browser falls back to whatever the device can deliver.
const VIDEO_CONSTRAINTS = {
  facingMode: { ideal: 'environment' },
  width: { ideal: 1920 },
  height: { ideal: 1080 }
}

// Phase 0b: the warehouse isn't fully labelled with LIT-N-A-N-N barcodes
// yet, so we accept any non-empty shelf code (e.g. "Row-G", "Oversized
// item area") up to 32 chars. Prism backend already auto-creates unknown
// shelves as large_bay. Once everything is properly labelled we can put
// a stricter regex back.
function normalize(value) {
  return (value || '').trim().replace(/\s+/g, ' ').toUpperCase()
}

function validate(value) {
  if (!value) return 'Enter a shelf code.'
  if (value.length > MAX_SHELF_CODE_LENGTH) {
    return `Shelf code is too long (max ${MAX_SHELF_CODE_LENGTH} characters).`
  }
  return null
}

// Module-level camera stream cache. iOS Safari re-prompts for camera
// permission on every getUserMedia() call once the previous stream has
// been .stop()'d — even within the same page session. We hold the
// stream alive across ScannerInput mounts so the operator only has to
// say "Allow" once per visit to the Putaway page. Putaway calls
// releaseCameraStream() on unmount to actually release the camera.
let cachedStream = null

function streamIsUsable(stream) {
  if (!stream || !stream.active) return false
  return stream.getVideoTracks().some(t => t.readyState === 'live')
}

async function getCameraStream() {
  if (streamIsUsable(cachedStream)) return cachedStream
  cachedStream = await navigator.mediaDevices.getUserMedia({ video: VIDEO_CONSTRAINTS })
  return cachedStream
}

export function releaseCameraStream() {
  if (!cachedStream) return
  cachedStream.getTracks().forEach(t => t.stop())
  cachedStream = null
}

function getVideoTrack(stream) {
  return stream?.getVideoTracks?.()[0] ?? null
}

function trackCapabilities(track) {
  return track?.getCapabilities?.() ?? {}
}

// Continuous autofocus is what fixes most "camera won't read this label"
// reports — without it phones lock focus on whatever they saw when the
// stream started and can't sharpen up when the operator brings the
// camera closer.
async function tryApplyContinuousFocus(track) {
  const caps = trackCapabilities(track)
  if (!caps.focusMode?.includes?.('continuous')) return
  try {
    await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] })
  } catch {
    // Some Androids advertise the capability but reject the constraint.
    // Not fatal — scanner still works at whatever the default focus is.
  }
}

async function setTrackTorch(track, enabled) {
  if (!track) return
  try {
    await track.applyConstraints({ advanced: [{ torch: enabled }] })
  } catch {
    // Caller already gated on caps.torch, but applyConstraints can still
    // race with device state on cheap Androids — swallow and move on.
  }
}

function ScannerInput({ placeholder = 'e.g. LIT-1-A-1-1 or Row-G', onResolve, onCancel }) {
  const [typed, setTyped] = useState('')
  const [error, setError] = useState(null)
  const [cameraError, setCameraError] = useState(null)
  const [torchAvailable, setTorchAvailable] = useState(false)
  const [torchOn, setTorchOn] = useState(false)
  const videoRef = useRef(null)
  const controlsRef = useRef(null)
  const trackRef = useRef(null)
  const onResolveRef = useRef(onResolve)

  useEffect(() => { onResolveRef.current = onResolve }, [onResolve])

  // Start camera scan on mount; stop decoder on unmount but keep the
  // stream alive (cached in module scope) so iOS Safari doesn't re-prompt
  // for permission on the next scan. facingMode: 'environment' picks the
  // REAR camera on phones — the default device index is usually the
  // front-facing one.
  useEffect(() => {
    const reader = new BrowserMultiFormatReader(ZXING_HINTS)
    let cancelled = false
    const video = videoRef.current

    async function start() {
      try {
        const stream = await getCameraStream()
        if (cancelled) return
        video.srcObject = stream

        const track = getVideoTrack(stream)
        trackRef.current = track
        if (track) {
          await tryApplyContinuousFocus(track)
          if (trackCapabilities(track).torch) setTorchAvailable(true)
        }

        const callback = (result) => {
          if (cancelled || !result) return
          const text = normalize(result.getText())
          if (!validate(text)) {
            onResolveRef.current(text)
          }
        }
        // decodeFromVideoElement (vs decodeFromStream / decodeFromConstraints)
        // intentionally does NOT dispose the stream on .stop() — the library
        // only manages decoding state, leaving stream lifecycle to us.
        const controls = await reader.decodeFromVideoElement(video, callback)
        controlsRef.current = controls
      } catch (e) {
        if (!cancelled) setCameraError(e.message || 'Camera unavailable')
      }
    }

    start()
    return () => {
      cancelled = true
      if (controlsRef.current?.stop) controlsRef.current.stop()
      // The cached stream survives this unmount, so if the operator left
      // the torch on it would stay lit until the page is closed. Force it
      // off here — re-mounts start with torchOn=false anyway.
      if (trackRef.current) setTrackTorch(trackRef.current, false)
      // Detach the stream from the <video> so the element doesn't hold a
      // reference, but DON'T stop the tracks — the cached stream is
      // reused on the next mount.
      if (video) video.srcObject = null
      reader.reset?.()
    }
  }, [])

  async function toggleTorch() {
    const track = trackRef.current
    if (!track) return
    const next = !torchOn
    setTorchOn(next)
    await setTrackTorch(track, next)
  }

  function tryResolve(value) {
    const v = normalize(value)
    const err = validate(v)
    if (err) {
      setError(err)
      return
    }
    setError(null)
    onResolve(v)
  }

  function onSubmit(e) {
    e.preventDefault()
    tryResolve(typed)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {cameraError ? (
        <div style={{
          padding: 12, borderRadius: 8, background: '#fef3c7',
          color: '#92400e', fontSize: 14
        }}>
          Camera unavailable — type the shelf id below.
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <video
            ref={videoRef}
            // iOS Safari: an active MediaStream attached to a <video>
            // holds the page in a "media-attached" user-activation state,
            // and the browser routes ambiguous nearby taps to the media
            // subsystem (PiP / scrubbing / control surface). That breaks
            // tap-to-focus on the shelf-id input below.
            //   - pointerEvents:'none' removes the video from hit-testing
            //     so iOS can't claim any tap as a media gesture.
            //   - disablePictureInPicture kills the PiP gesture path
            //     entirely (long-press / two-finger affordances).
            //   - tabIndex=-1 makes sure the video can never steal focus.
            // The decoder still reads frames; only user interaction is
            // disabled, which is what we want — there's nothing to tap.
            style={{
              width: '100%', aspectRatio: '4/3', background: '#000',
              borderRadius: 8, display: 'block', pointerEvents: 'none'
            }}
            autoPlay muted playsInline
            disablePictureInPicture
            tabIndex={-1}
          />
          {/* Live-scan hint so operators don't expect a capture button. */}
          <div style={{
            position: 'absolute',
            top: 8, left: 8, right: 8,
            padding: '6px 10px',
            background: 'rgba(0,0,0,0.6)',
            color: 'white',
            fontSize: 13,
            borderRadius: 6,
            textAlign: 'center',
            pointerEvents: 'none'
          }}>
            Hold the camera over the shelf label — it scans automatically
          </div>
          {torchAvailable && (
            <button
              type="button"
              onClick={toggleTorch}
              aria-pressed={torchOn}
              aria-label={torchOn ? 'Turn flashlight off' : 'Turn flashlight on'}
              style={{
                position: 'absolute',
                bottom: 8, right: 8,
                minHeight: 40, padding: '8px 14px',
                background: torchOn ? '#fbbf24' : 'rgba(0,0,0,0.6)',
                color: torchOn ? '#1f2937' : 'white',
                border: 'none', borderRadius: 6,
                fontSize: 14, fontWeight: 600,
                cursor: 'pointer',
                touchAction: 'manipulation'
              }}
            >
              {torchOn ? 'Light: on' : 'Light: off'}
            </button>
          )}
        </div>
      )}

      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* No autoFocus: on iOS Safari, programmatic focus without a user
            gesture leaves the input "focused" but the keyboard hidden, and
            the next tap on the already-focused input fires no focus event,
            so the keyboard never appears. The operator has to tap the
            input to bring up the keyboard, which iOS handles correctly. */}
        <input
          type="text"
          className="form-input"
          placeholder={placeholder}
          value={typed}
          inputMode="text"
          autoCapitalize="characters"
          maxLength={MAX_SHELF_CODE_LENGTH}
          enterKeyHint="go"
          onChange={(e) => { setTyped(e.target.value); if (error) setError(null) }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); tryResolve(typed) } }}
          // Defensive against iOS Safari's media-attached activation
          // state (see the <video> comment): calling focus() synchronously
          // inside touchend reuses the live user gesture, so iOS pops
          // the keyboard even when the native click→focus path is
          // swallowed by the media subsystem. No preventDefault — we
          // still want the synthetic click to fire so React's standard
          // focus events run too.
          onTouchEnd={(e) => {
            if (document.activeElement !== e.currentTarget) {
              e.currentTarget.focus()
            }
          }}
          style={{
            fontSize: 18, padding: '12px', minHeight: 48,
            // Kill any residual 300ms tap delay on iOS — every touch
            // should immediately register as a tap.
            touchAction: 'manipulation'
          }}
        />
        {error && (
          <div style={{ color: '#dc2626', fontSize: 14 }}>{error}</div>
        )}
        <div style={{ display: 'flex', gap: 12 }}>
          <button type="button" className="btn btn-secondary" onClick={onCancel} style={{ minHeight: 48, flex: 1 }}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" style={{ minHeight: 48, flex: 2 }}>
            Submit
          </button>
        </div>
      </form>
    </div>
  )
}

export default ScannerInput
